import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, MapPin, Calendar, ArrowRight, Search, Filter, User } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { parcelsApi } from '../../lib/api';
import { formatDate, formatPrice } from '../../lib/utils';
import { toast } from 'sonner';
import { VerifiedBadge } from '../../components/VerifiedBadge';

const AvailableMissionsPage = () => {
  const [parcels, setParcels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [accepting, setAccepting] = useState(null);

  useEffect(() => {
    fetchParcels();
  }, []);

  const fetchParcels = async () => {
    try {
      const response = await parcelsApi.getAvailable();
      setParcels(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (parcelId) => {
    setAccepting(parcelId);
    try {
      await parcelsApi.accept(parcelId);
      toast.success('Mission acceptée!');
      fetchParcels();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setAccepting(null);
    }
  };

  const filteredParcels = parcels.filter(parcel => 
    search === '' || 
    parcel.pickup_address.city.toLowerCase().includes(search.toLowerCase()) ||
    parcel.delivery_address.city.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="available-missions-page">
      <div>
        <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-900 uppercase tracking-tight">
          Missions disponibles
        </h1>
        <p className="text-slate-600 mt-1">Trouvez des livraisons près de chez vous</p>
      </div>

      {/* Search */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Rechercher par ville..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Missions List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="spinner mx-auto"></div>
          <p className="text-slate-500 mt-4">Chargement...</p>
        </div>
      ) : filteredParcels.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-12 text-center">
            <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 text-lg">Aucune mission disponible pour le moment</p>
            <p className="text-slate-400 text-sm mt-2">Revenez plus tard pour découvrir de nouvelles opportunités</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredParcels.map((parcel) => (
            <Card key={parcel.id} className="border-slate-200 hover:shadow-md transition-shadow" data-testid={`mission-${parcel.id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  {/* Route */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase">Départ</p>
                        <p className="font-medium text-slate-900">{parcel.pickup_address.city}</p>
                        <p className="text-sm text-slate-500">{parcel.pickup_address.postal_code}</p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-slate-400 mx-2" />
                      <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase">Arrivée</p>
                        <p className="font-medium text-slate-900">{parcel.delivery_address.city}</p>
                        <p className="text-sm text-slate-500">{parcel.delivery_address.postal_code}</p>
                      </div>
                    </div>
                    
                    {/* Details */}
                    <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {parcel.sender_name}
                        <VerifiedBadge verified={parcel.sender_verified} size="sm" />
                      </span>
                      <span className="flex items-center gap-1">
                        <Package className="w-4 h-4" />
                        {parcel.weight} kg
                      </span>
                      <span>{parcel.distance_km} km</span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(parcel.pickup_date)}
                      </span>
                    </div>
                    
                    {parcel.description && (
                      <p className="text-sm text-slate-500 mt-2 truncate max-w-md">
                        {parcel.description}
                      </p>
                    )}
                  </div>

                  {/* Price & Action */}
                  <div className="text-right border-t md:border-t-0 md:border-l border-slate-200 pt-4 md:pt-0 md:pl-6">
                    <p className="text-3xl font-bold text-green-600">{formatPrice(parcel.proposed_price || parcel.base_price)}</p>
                    <p className="text-sm text-slate-500 mb-4">budget expéditeur</p>
                    <div className="flex gap-2">
                      <Link to={`/dashboard/parcel/${parcel.id}`}>
                        <Button variant="outline" size="sm" className="border-slate-200">
                          Détails
                        </Button>
                      </Link>
                      <Button 
                        size="sm"
                        className="bg-orange-500 hover:bg-orange-600"
                        onClick={() => handleAccept(parcel.id)}
                        disabled={accepting === parcel.id}
                        data-testid={`accept-btn-${parcel.id}`}
                      >
                        {accepting === parcel.id ? 'Acceptation...' : 'Accepter'}
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AvailableMissionsPage;
